import React, {FC} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'

import MobxCheckbox from 'components/elements/fields/MobxCheckbox/MobxCheckbox'
import s from 'src/components/blocks/forms/Form1099/Form1099Table/Form1099Table.module.scss'
import MobxSelect from 'src/components/elements/fields/MobxSelect'
import MobxTextInput from 'src/components/elements/fields/MobxTextInput'
import usStates from 'src/configs/usStates'
import Form1099Store from 'src/models/Form1099/Form1099.store'
import {getStringValue} from 'src/utils'
import {useForm1099Store} from 'src/utils/contexts/RootStore.context'
const cx = cn.bind(s)

interface IForm1099TableProps {
	data: Form1099Store
	className?: string
}

const Form1099Table: FC<IForm1099TableProps> = ({data, className}) => {
	const form1099 = useForm1099Store()
	const {requiredFields, fields} = data

	const onIncomeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		form1099.fields.otherIncome = getStringValue(e.target.value)
		form1099.fields.income = fields.otherIncome
	}

	return (
		<div className={cx('scroll-table', className)}>
			<table className={cx('table')}>
				<tbody>
					<tr>
						<td colSpan={5} className={cx('align-middle', 'column--correct')}>
							<MobxCheckbox
								label="CORRECTED (if checked)"
								value={false}
								name="corrected"
								store={form1099.fields}
							/>
						</td>
					</tr>
					<tr>
						<td
							rowSpan={3}
							colSpan={2}
							className={cx('column', 'column--first', 'column--padding')}>
							<MobxTextInput
								wrapperClassName={cx('column__field')}
								label={
									"PAYER'S name, street address, city or town, state or province, country, ZIP or foreign postal code, and telephone no."
								}
								placeholder="Company name"
								store={requiredFields}
								name="payersName"
								variant="editable"
								required
							/>
							<MobxTextInput
								wrapperClassName={cx('column__field')}
								placeholder="Address"
								store={requiredFields}
								name="payersAddress"
								variant="editable"
								required
							/>
							<div className={cx('column__fields')}>
								<MobxTextInput
									wrapperClassName={cx('column__field')}
									placeholder="City"
									store={requiredFields}
									name="payersCity"
									variant="editable"
									required
								/>
								<MobxSelect
									wrapperClassName={cx('column__field')}
									name="payersState"
									placeholder="State"
									store={requiredFields}
									options={usStates}
									variant="editable"
									data-testid="payersState"
									required
								/>
								<MobxTextInput
									wrapperClassName={cx('column__field')}
									placeholder="ZIP code"
									store={requiredFields}
									name="payersZip"
									variant="editable"
									required
								/>
							</div>
						</td>
						<td className={cx('column', 'column--second')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>1</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Rents'}
										placeholder="$ 0.00"
										store={fields}
										name="rents"
										type="text"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td rowSpan={2} className={cx('column', 'column--third', 'column--text')}>
							<div className={cx('text__omb')}>OMB No.1545-0008</div>
							<div className={cx('text__year')}>
								{fields?.year.getFullYear() || new Date().getFullYear()}
							</div>
							<div className={cx('text__fields')}>
								Form <strong>1099-MISC</strong>
							</div>
						</td>
						<td rowSpan={2} className={cx('column', 'column--fourth')}>
							<div className={cx('text__income')}>
								Miscellaneous
								<br />
								Income
							</div>
						</td>
					</tr>
					<tr>
						<td className={cx('column', 'column--second')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>2</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Royalties'}
										placeholder="$ 0.00"
										store={fields}
										name="royalties"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
					</tr>
					<tr>
						<td className={cx('column', 'column--second', 'column--other-income')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>3</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Other income'}
										placeholder="$ 0.00"
										store={fields}
										name="otherIncome"
										isFormatText
										shouldHandleOldValue
										isNumber
										onBlur={onIncomeBlur}
									/>
								</div>
							</div>
						</td>
						<td className={cx('column', 'column--second', 'column--border-weight')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>4</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Federal Income tax withheld'}
										placeholder="$ 0.00"
										store={fields}
										name="federalIncomeTax"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td rowSpan={2} className={cx('column', 'column--second')}>
							<div className={cx('text__copy')}>
								Copy B<br />
								For Recipient
							</div>
						</td>
					</tr>
					<tr>
						<td className={cx('column', 'column--tin')}>
							<MobxTextInput
								label={"PAYER'S TIN (Company EIN)"}
								store={fields}
								name="companyEin"
								variant="editable"
							/>
						</td>
						<td className={cx('column', 'column--tin')}>
							<MobxTextInput
								label={"RECIPIENT'S TIN (Social Security)"}
								store={fields}
								name="socialSecurity"
								variant="editable"
							/>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>5</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Fishing boat proceeds'}
										placeholder="$ 0.00"
										store={fields}
										name="fishingBoat"
										type="text"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td className={cx('column', 'column--medical-care')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>6</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Medical and health care payments'}
										placeholder="$ 0.00"
										store={fields}
										name="medicalAndHealthCare"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
					</tr>
					<tr>
						<td
							rowSpan={3}
							colSpan={2}
							className={cx('column', 'column--first', 'column--padding')}>
							<MobxTextInput
								wrapperClassName={cx('column__field')}
								label={
									"RECIPIENT'S name, street address, city or town, state or province, country, ZIP or foreign postal code"
								}
								placeholder="Name"
								store={requiredFields}
								name="recipientsName"
								variant="editable"
								required
							/>
							<MobxTextInput
								wrapperClassName={cx('column__field')}
								placeholder="Address"
								store={requiredFields}
								name="recipientsAddress"
								variant="editable"
								required
							/>
							<div className={cx('column__fields')}>
								<MobxTextInput
									wrapperClassName={cx('column__field')}
									placeholder="City"
									store={requiredFields}
									name="recipientsCity"
									variant="editable"
									required
								/>
								<MobxSelect
									wrapperClassName={cx('column__field')}
									name="recipientsState"
									placeholder="State"
									store={requiredFields}
									options={usStates}
									variant="editable"
									data-testid="recipientsState"
									required
								/>
								<MobxTextInput
									wrapperClassName={cx('column__field')}
									placeholder="ZIP code"
									store={requiredFields}
									name="recipientsZip"
									variant="editable"
									required
								/>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>7</div>
								<div className={cx('column__input')}>
									<MobxCheckbox
										classNameWrapper={cx('column__check', 'column__check-payer')}
										label="Payer made direct sales of $5.000 or more of consumer products to a buyer (recipient) for resale"
										value={fields.payerDirectSales}
										name="payerDirectSales"
										store={form1099.fields}
									/>
								</div>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>8</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Subsitute payments in lieu of dividends or interest'}
										placeholder="$ 0.00"
										store={fields}
										name="substitutePayments"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td rowSpan={4} className={cx('column')}>
							<div className={cx('text__info')}>
								This is important tax information and is being furnished to the IRS. If you are
								required to file a return, a negligence penalty or other sanction may be imposed on
								you if this income is taxable and the IRS determines that it has not been reported.
							</div>
						</td>
					</tr>
					<tr>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>9</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Crop insurance proceeds'}
										placeholder="$ 0.00"
										store={fields}
										name="cropInsurance"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>10</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Gross proceeds paid to an attorney'}
										placeholder="$ 0.00"
										store={fields}
										name="grossProceeds"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
					</tr>
					<tr>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>11</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										placeholder="$ 0.00"
										store={fields}
										name="field11"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>12</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Section 409A deferrals'}
										placeholder="$ 0.00"
										store={fields}
										name="section409A"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
					</tr>
					<tr>
						<td className={cx('column', 'column--account-number')}>
							<div className={cx('column__content')}>
								<div className={cx('column__input')}>
									<MobxTextInput
										label={
											<>
												Account number (see instructio
												<br className="d-none d-md-block d-lg-none" />
												ns)
											</>
										}
										store={fields}
										name="accountNumber"
									/>
								</div>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__input')}>
									<MobxCheckbox
										classNameWrapper={cx('column__check', 'column__check-fatca')}
										label={
											<>
												FATCA filling requirm
												<br className="d-none d-md-block d-lg-none" />
												ent
											</>
										}
										value={fields.fatca}
										name="fatca"
										store={form1099.fields}
									/>
								</div>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>13</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'Excess golden parachute payments'}
										placeholder="$ 0.00"
										store={fields}
										name="excessGoldenParachutePayments"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>14</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'NonQualified deferred compensation'}
										placeholder="$ 0.00"
										store={fields}
										name="nonQualifiedDeferredCompensation"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
					</tr>
					<tr>
						<td colSpan={2} rowSpan={2} className={cx('column')} />
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>15</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'State tax withheld'}
										placeholder="$ 0.00"
										store={fields}
										name="stateTaxWithHeldA"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>16</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={"State/Payer's state no."}
										placeholder=""
										store={fields}
										name="payersStateNoA"
										type="text"
									/>
								</div>
							</div>
						</td>
						<td className={cx('column')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark')}>17</div>
								<div className={cx('column__input')}>
									<MobxTextInput
										className={cx('ta-right')}
										label={'State income'}
										placeholder="$ 0.00"
										store={fields}
										name="stateIncomeA"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
					</tr>
					<tr>
						<td className={cx('column', 'column--border-dashed')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark', 'column__mark--hidden')}>15</div>
								<div className={cx('column__input', 'column__input--bottom')}>
									<MobxTextInput
										className={cx('ta-right')}
										placeholder="$ 0.00"
										store={fields}
										name="stateTaxWithHeldB"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
						<td className={cx('column', 'column__border-dashed')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark', 'column__mark--hidden')}>16</div>
								<div className={cx('column__input', 'column__input--bottom')}>
									<MobxTextInput
										className={cx('ta-right')}
										placeholder=""
										store={fields}
										name="payersStateNoB"
									/>
								</div>
							</div>
						</td>
						<td className={cx('column', 'column__border-dashed')}>
							<div className={cx('column__content')}>
								<div className={cx('column__mark', 'column__mark--hidden')}>17</div>
								<div className={cx('column__input', 'column__input--bottom')}>
									<MobxTextInput
										className={cx('ta-right')}
										placeholder="$ 0.00"
										store={fields}
										name="stateIncomeB"
										isFormatText
										shouldHandleOldValue
										isNumber
									/>
								</div>
							</div>
						</td>
					</tr>
					<tr>
						<td colSpan={5} className={cx('column', 'column--footer')}>
							<div>
								<div className={cx('text__form')}>
									Form <strong>1099-MISC &nbsp;&nbsp;</strong>
									<br className="d-none d-md-block d-lg-none" /> (keep for your records)
								</div>

								<a
									rel="noopener noreferrer"
									href="https://www.irs.gov/forms-pubs/about-form-1099-misc"
									target="_blank"
									className={cx('text__form', 'text__url')}>
									www.irs.gov/formMISC
								</a>

								<div className={cx('text__form', 'ta-right')}>
									Department of the Treasury - Internal{' '}
									<br className="d-none d-md-block d-lg-none" />
									Revenue Service
								</div>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	)
}

Form1099Table.displayName = 'Form1099Table'

export default observer(Form1099Table)
